import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Observable } from 'rxjs';

import { DashboardDevice, DashboardInstallationData, SubscriptionPlan } from '@vi-iot/client';

import {
  InstallationType,
  KanbanBoardHelperService,
  KanbanBoardStore,
  KanbanColumnPrimaryData,
  Platform,
  UpgradePackageButtonData,
  limitOfSelfDefinedColumns,
  standaloneImagePath,
} from '@store';

import { PillComponent } from '@components';
import { PillType } from '../../../shared/components/pill/pill.model';
import { CommonStandaloneModule } from '../../../shared/common-standalone.module';
import { BookingPackageBadgeComponent } from '../../../shared/components/booking-package-badge/booking-package-badge.component';
import { InstallationTileBadgesComponent } from './installation-tile-badges/installation-tile-badges.component';
import { InstallationTileDeviceBadgesComponent } from './installation-tile-device-badges/installation-tile-device-badges.component';
import { HighestSubscriptionPlanPipe } from '../pipes/highest-subscription-plan/highest-subscription-plan.pipe';
import { LetDirective } from '@ngrx/component';
import { BrandLogo } from '../../../+installations/helpers/brand-logo';

@Component({
  selector: 'app-kanban-installation-list-tile',
  standalone: true,
  templateUrl: './kanban-installation-list-tile.component.html',
  styleUrls: ['./kanban-installation-list-tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonStandaloneModule,
    PillComponent,
    MatIconModule,
    MatMenuModule,
    BookingPackageBadgeComponent,
    MatButtonModule,
    InstallationTileBadgesComponent,
    InstallationTileDeviceBadgesComponent,
    HighestSubscriptionPlanPipe,
    LetDirective,
  ],
})
export class KanbanInstallationListTileComponent implements OnInit {
  @Input() installation: DashboardInstallationData;
  @Input() isColumnSelfDefined: boolean;
  @Input() columnId: number;
  @Input() columnIndex: number;

  readonly pillType = PillType;
  readonly installationType = InstallationType;
  readonly defaultQuantityOfVisibleDevices = 3;
  readonly subscriptionPlan = SubscriptionPlan;
  readonly limitOfSelfDefinedColumns = limitOfSelfDefinedColumns;
  readonly standaloneImagePath = standaloneImagePath;

  mainDevice: DashboardDevice;
  selfMadeColumnsWithoutInstallations$: Observable<KanbanColumnPrimaryData[]>;
  isSingleDevice: boolean;
  isShowingMoreDevices = false;
  upgradePackageData$: Observable<UpgradePackageButtonData>;
  isMainDeviceBadgeVisible: boolean;
  imagePath: string;
  isPackageBadgeVisible: boolean;
  installationHeaderLabel: string;
  brandLogo: string;

  get componentClientHeight(): number {
    return this.elRef.nativeElement.clientHeight;
  }

  constructor(
    public kanbanBoardStore: KanbanBoardStore,
    public kanbanBoardHelperService: KanbanBoardHelperService,
    private elRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.mainDevice = this.kanbanBoardHelperService.getMainDevice(this.installation);
    this.isSingleDevice = this.kanbanBoardHelperService.isSingleDevice(this.installation);
    this.upgradePackageData$ = this.kanbanBoardHelperService.getUpgradePackageData(
      this.installation
    );
    this.isMainDeviceBadgeVisible = this.kanbanBoardHelperService.calculateIsMainPackageBadgeVisible(
      this.installation
    );
    this.imagePath = this.kanbanBoardHelperService.calculateImagePath(this.installation);
    this.installationHeaderLabel = this.kanbanBoardHelperService.getInstallationHeaderLabel(
      this.installation
    );
    if (this.installation) {
      this.installation = BrandLogo.updateDashboardInstallationDataBrandLogo(this.installation);
    }
    const deviceRoles = this.installation?.devices[0]?.roles;
    this.brandLogo = BrandLogo.getBrandLogo(deviceRoles);
  }

  openManageMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.selfMadeColumnsWithoutInstallations$ = this.kanbanBoardStore.columnsAvailableForInstallation(
      this.installation.id
    );
  }

  manageDevicesVisibility(event: MouseEvent, value: boolean): void {
    event.stopPropagation();
    this.isShowingMoreDevices = value;
  }

  onMenuClosed(): void {
    this.selfMadeColumnsWithoutInstallations$ = undefined;
  }

  createNewColumn(): void {
    if (this.isColumnSelfDefined) {
      this.kanbanBoardStore.createInputForNewColumnBeforeMovingInstallation(
        this.installation,
        this.columnId
      );
    } else {
      this.kanbanBoardStore.createInputForNewColumnBeforeCopyingInstallation(this.installation);
    }
  }

  handleColumnAction(column: KanbanColumnPrimaryData): void {
    if (this.isColumnSelfDefined) {
      this.kanbanBoardStore.moveKanbanInstallation(this.installation, column.id, this.columnId);
    } else {
      this.kanbanBoardStore.copyKanbanInstallation(this.installation, column.id, this.columnId);
    }
  }

  removeInstallationDuplication(): void {
    this.kanbanBoardStore.deleteKanbanInstallationCopy(this.columnId, this.installation.id);
  }

  openMainDeviceQuickView(): void {
    const mainDeviceIndex = 0;
    this.kanbanBoardStore.openQuickViewPanel(this.installation, mainDeviceIndex);
  }

  openDeviceQuickView(event: MouseEvent, deviceIndex: number): void {
    event.stopPropagation();

    this.kanbanBoardStore.openQuickViewPanel(this.installation, deviceIndex);
  }

  navigateToBooking(event: MouseEvent, platform: Platform): void {
    event.stopPropagation();
    this.kanbanBoardStore.navigateToBooking(this.installation, platform);
  }
}
